import { useCallback, useMemo, useState } from 'react';
import Text from '../../../components/kit/text/Text';
import Steps from '../../../components/kit/steps/Steps';
import { Question } from 'src/types/questionnaire.types';
import styles from './Questionnaire.module.css';
import {
  SingleSelectionQuestion,
  DateSelectionQuestion,
  DateTimeSelectionQuestion,
  OptionSelectionQuestion,
} from './questions';
import { InputQuestion } from './questions/InputQuestion';
import AnimatedElement from 'src/app/components/kit/animated-container/AnimatedElement';

type QuestionnaireProps = {
  questions: Question[];
  onSaveSelections: (result: Question[]) => void;
  onClose: () => void;
  show: boolean;
};

const Questionnaire = ({
  questions,
  show,
  onSaveSelections,
  onClose,
}: QuestionnaireProps) => {
  const [answers, setAnswers] = useState<Question[]>([]);
  const [stepsCompleted, setStepsCompleted] = useState<number[]>([]);
  const onChoiceSelect = (result: Question, stepNum: number) => {
    let currentStepCompleted = true;
    const { date, choices, selections, id } = result;
    const answerPreviousSelection = answers.find((el) => el.id === id);
    const filteredSelections = answers.filter((el) => el.id !== id);
    const currentSelection = {
      ...result,
      date: date ?? answerPreviousSelection?.date,
      choices:
        choices?.length === 1 ? choices : answerPreviousSelection?.choices,
      selections:
        selections?.length === 1
          ? selections
          : answerPreviousSelection?.selections,
    };
    setAnswers([...filteredSelections, currentSelection]);
    if (result.isDoctorAppointmentCreation) {
      const choice = currentSelection.choices
        ? currentSelection.choices[0]
        : undefined;
      const doctorType = currentSelection.selections
        ? currentSelection.selections[0]
        : undefined;
      const appointmentDate = currentSelection.date;
      currentStepCompleted =
        choice?.title?.toLocaleLowerCase() === 'no' ||
        (choice?.title?.toLocaleLowerCase() === 'yes' &&
          Boolean(doctorType) &&
          Boolean(appointmentDate));
    }
    if (currentStepCompleted) {
      setStepsCompleted((prev) => [...prev, stepNum]);
    } else {
      setStepsCompleted((prev) => prev.filter((step) => step !== stepNum));
    }
  };
  const steps = useMemo(() => {
    const result: Record<number, () => React.JSX.Element> = {};
    for (let i = 0; i < questions.length; i++) {
      result[i + 1] = () => (
        <div className={styles.wrapper}>
          <Text tag='h2' size='m-large' weight='bold'>
            {questions[i].title}
          </Text>
          {questions[i].type.includes('single-choice') && (
            <SingleSelectionQuestion
              stepNumber={i + 1}
              question={questions[i]}
              handleSelectAnswer={onChoiceSelect}
              selectedOption={
                answers.find((question) => question.id === questions[i].id)
                  ?.choices
              }
            />
          )}
          {questions[i].type.includes('input') && (
            <InputQuestion
              stepNumber={i + 1}
              question={questions[i]}
              handleSelectAnswer={onChoiceSelect}
              selectedOption={
                answers.find((question) => question.id === questions[i].id)
                  ?.choices
              }
            />
          )}
          {questions[i].type.includes('date-choice') && (
            <DateSelectionQuestion
              stepNumber={i + 1}
              question={questions[i]}
              selectedOption={
                answers.find((question) => question.id === questions[i].id)
                  ?.date
              }
              maxDate={
                questions[i].title === 'When were you first prescribed Vydura?'
                  ? new Date()
                  : undefined
              }
              minDate={
                questions[i].title === 'What day did you first take Vydura?'
                  ? answers.find(
                      (answser) =>
                        answser.title ===
                        'When were you first prescribed Vydura?'
                    )?.date
                  : undefined
              }
              handleSelectAnswer={onChoiceSelect}
            />
          )}
          {questions[i].type.includes('date-time-selection') && (
            <AnimatedElement
              name='dateTimeSelection'
              delay={0.3}
              isVisible={Boolean(
                answers
                  .find((question) => question.id === questions[i].id)
                  ?.choices?.find((choice) => choice.title === 'Yes')
              )}
            >
              <div>
                <DateTimeSelectionQuestion
                  stepNumber={i + 1}
                  question={questions[i]}
                  selectedOption={
                    answers.find((question) => question.id === questions[i].id)
                      ?.date
                  }
                  handleSelectAnswer={onChoiceSelect}
                />
              </div>
            </AnimatedElement>
          )}
          {questions[i].type.includes('option-selection') && (
            <AnimatedElement
              delay={0.3}
              name='optSelection'
              isVisible={Boolean(
                answers
                  .find((question) => question.id === questions[i].id)
                  ?.choices?.find((choice) => choice.title === 'Yes') &&
                  answers.find((question) => question.id === questions[i].id)
                    ?.date
              )}
            >
              <OptionSelectionQuestion
                stepNumber={i + 1}
                question={questions[i]}
                selectedOption={
                  answers.find((question) => question.id === questions[i].id)
                    ?.selections
                }
                handleSelectAnswer={onChoiceSelect}
              />
            </AnimatedElement>
          )}
        </div>
      );
    }
    return result;
  }, [questions, answers]);
  const onContinue = useCallback(
    () => onSaveSelections(answers),
    [onSaveSelections, answers]
  );

  return (
    <Steps
      steps={steps}
      stepsCompleted={stepsCompleted}
      show={show}
      onContinue={onContinue}
      onClose={onClose}
    />
  );
};

export default Questionnaire;
